import {
  ConfigureOptionsParams,
  FilterPassParams,
  AdvancedFilterName,
  IFilterComparator,
  FilterOptions,
  CompoundBooleanFilterModel,
  AppliedFilterItem
} from '../filter-types';
import { FilterResultFacade } from '../filter-result-facade';
import { FilterHandlerBase } from './base/filter-handler-base';
import { DefaultComparator } from './comparators/default-comparator';
import { DealVersion } from '../../../types/deal.types';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../../state/calendar-filter/state';
import {
  FilterChange,
  FilterClear
} from '../../../state/calendar-filter/actions';

export class CombinedDocumentsFilterHandler extends FilterHandlerBase {
  private DOCUMENTS_LABEL = 'Documents';
  private ROADSHOW_LABEL = 'Roadshow';

  private comparator: IFilterComparator<any>;

  private stringAll = 'All';

  get name(): string {
    return AdvancedFilterName.combinedDocuments;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  configureFilterOptions(params: ConfigureOptionsParams): void {
    params.setFilterService.combinedDocuments =
      params.filterOptions.combinedDocuments;
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return filterOptions.combinedDocuments;
  }

  hasValidDiff(options: FilterOptions, grid: any, store: any): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(grid, store);
  }

  getValidDiffComparator(options: FilterOptions): IFilterComparator<any> {
    if (!this.comparator) {
      this.comparator = new DefaultComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade) {
    const isCombinedDocuments = params.filters[this.name];

    const calcFilterPass = this.calculateFilterPass(
      params.deal,
      isCombinedDocuments.model
    );

    result.applyDealFilerResult(params.deal.id, this.name, calcFilterPass);
    return calcFilterPass;
  }

  calculateFilterPass(
    deal: DealVersion,
    model: CompoundBooleanFilterModel
  ): boolean {
    if (model.boolean1 === undefined && model.boolean2 === undefined) {
      return true;
    }
    const isThereADocumentsURL = this.hasDocuments(deal);
    const isThereARoadshowURL: boolean = this.hasRoadshow(deal);
    const isDocumentsRadioAllOrYes: boolean =
      model.boolean1 === undefined ? true : model.boolean1;
    const isRoadshowRadioAllOrYes: boolean =
      model.boolean2 === undefined ? true : model.boolean2;

    const result =
      this.filterDocumentsAndRoadshow(
        isThereADocumentsURL,
        isThereARoadshowURL,
        isDocumentsRadioAllOrYes,
        isRoadshowRadioAllOrYes
      ) ||
      this.filterNoDocumentsAndNoRoadshow(
        isThereADocumentsURL,
        isThereARoadshowURL,
        isDocumentsRadioAllOrYes,
        isRoadshowRadioAllOrYes,
        model
      ) ||
      this.filterDocumentsAndNoRoadshow(
        isThereADocumentsURL,
        isThereARoadshowURL,
        isDocumentsRadioAllOrYes,
        isRoadshowRadioAllOrYes,
        model
      ) ||
      this.filterNoDocumentsAndRoadshow(
        isThereADocumentsURL,
        isThereARoadshowURL,
        isDocumentsRadioAllOrYes,
        isRoadshowRadioAllOrYes,
        model
      );
    return result ? result.result : true;
  }

  private filterDocumentsAndRoadshow(
    isThereADocumentsURL: boolean,
    isThereARoadshowURL: boolean,
    isDocumentsRadioAllOrYes: boolean,
    isRoadshowRadioAllOrYes: boolean
  ): { result: boolean } {
    if (isThereADocumentsURL && isThereARoadshowURL) {
      return {
        result: !(!isDocumentsRadioAllOrYes || !isRoadshowRadioAllOrYes)
      };
    }
    return undefined;
  }

  private filterNoDocumentsAndNoRoadshow(
    isThereADocumentsURL: boolean,
    isThereARoadshowURL: boolean,
    isDocumentsRadioAllOrYes: boolean,
    isRoadshowRadioAllOrYes: boolean,
    model: CompoundBooleanFilterModel
  ): { result: boolean } {
    if (!isThereADocumentsURL && !isThereARoadshowURL) {
      if (!isDocumentsRadioAllOrYes && !isRoadshowRadioAllOrYes) {
        return { result: true };
      }
      if (!isDocumentsRadioAllOrYes || !isRoadshowRadioAllOrYes) {
        return {
          result:
            (!isDocumentsRadioAllOrYes && model.boolean2 === undefined) ||
            (!isRoadshowRadioAllOrYes && model.boolean1 === undefined)
        };
      }
      return { result: false };
    }

    if (isThereADocumentsURL && isThereARoadshowURL) {
      return {
        result: !(!isDocumentsRadioAllOrYes || !isRoadshowRadioAllOrYes)
      };
    }
    return undefined;
  }

  private filterDocumentsAndNoRoadshow(
    isThereADocumentsURL: boolean,
    isThereARoadshowURL: boolean,
    isDocumentsRadioAllOrYes: boolean,
    isRoadshowRadioAllOrYes: boolean,
    model: CompoundBooleanFilterModel
  ): { result: boolean } {
    if (isThereADocumentsURL && !isThereARoadshowURL) {
      if (!isDocumentsRadioAllOrYes && !isRoadshowRadioAllOrYes) {
        return { result: false };
      }
      return {
        result:
          (isDocumentsRadioAllOrYes && !model.boolean2) ||
          (model.boolean1 && !isRoadshowRadioAllOrYes)
      };
    }
    return undefined;
  }

  private filterNoDocumentsAndRoadshow(
    isThereADocumentsURL: boolean,
    isThereARoadshowURL: boolean,
    isDocumentsRadioAllOrYes: boolean,
    isRoadshowRadioAllOrYes: boolean,
    model: CompoundBooleanFilterModel
  ): { result: boolean } {
    if (!isThereADocumentsURL && isThereARoadshowURL) {
      return {
        result:
          (!model.boolean1 && isRoadshowRadioAllOrYes) ||
          (!isDocumentsRadioAllOrYes && model.boolean2)
      };
    }
    return undefined;
  }

  private hasDocuments(deal): boolean {
    return (
      (!!deal.posURL && !!deal.posURL.length) ||
      (!!deal.documents && !!deal.documents.length)
    );
  }

  private hasRoadshow(deal): boolean {
    return !!deal.roadshowURL && !!deal.roadshowURL.length;
  }

  getAppliedFiltersItems(filter: CalendarFilter): AppliedFilterItem[] {
    const result: AppliedFilterItem[] = [];

    if (filter.model.isFilterActive) {
      if (filter.model.boolean1 !== undefined) {
        result.push({
          name: this.name,
          label: this.DOCUMENTS_LABEL,
          filterLabel: this.DOCUMENTS_LABEL,
          filterValue: filter.model.boolean1 ? 'Yes' : 'No'
        });
      }
      if (filter.model.boolean2 !== undefined) {
        result.push({
          name: this.name,
          label: this.ROADSHOW_LABEL,
          filterLabel: this.ROADSHOW_LABEL,
          filterValue: filter.model.boolean2 ? 'Yes' : 'No'
        });
      }
    }

    return result;
  }

  filterClear(
    filterChange: FilterClear,
    state: CalendarFiltersState
  ): FilterChange {
    const result = { name: this.name, value: undefined };
    if (filterChange.name === this.name) {
      const filterName = filterChange.name;
      const subFilterName = filterChange.subFilterName;
      const filter = { ...state[filterName] };
      const model = { ...filter.model };
      let [stringModel1, stringModel2] = model.stringModel.split(', ');

      if (subFilterName === this.DOCUMENTS_LABEL) {
        model.boolean1 = undefined;
        stringModel1 = this.stringAll;
      } else if (subFilterName === this.ROADSHOW_LABEL) {
        model.boolean2 = undefined;
        stringModel2 = this.stringAll;
      } else {
        model.boolean1 = undefined;
        model.boolean2 = undefined;
        stringModel1 = this.stringAll;
        stringModel2 = this.stringAll;
      }

      model.stringModel = [stringModel1, stringModel2].join(', ');
      result.value =
        model.boolean1 === undefined && model.boolean2 === undefined
          ? undefined
          : { ...filter, model };
    }
    return result;
  }
}
